import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HardDrive, ShieldCheck, Download, Upload } from 'lucide-react';
import { SiteHeader } from '../components/layout/SiteHeader';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { useAuthStore } from '../state/authStore';
import { useUiStore } from '../state/uiStore';
import {
  getStorageEstimate,
  requestPersistentStorage,
  type StorageEstimateInfo,
} from '../storage/persistence';
import { listProjectMetas, loadProject } from '../storage/projectRepository';
import { exportAllProjectsBackup, importProjectBackup } from '../storage/backup';
import { downloadBlob } from '../lib/download';
import { formatBytes } from '../lib/format';
import { t } from '../i18n';
import './account-page.css';

export default function AccountPage() {
  const navigate = useNavigate();
  const authStatus = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const pushToast = useUiStore((s) => s.pushToast);

  const [estimate, setEstimate] = useState<StorageEstimateInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getStorageEstimate().then(setEstimate);
  }, []);

  const handleRequestPersist = async () => {
    const granted = await requestPersistentStorage();
    const updated = await getStorageEstimate();
    setEstimate(updated);
    pushToast({
      kind: granted ? 'success' : 'info',
      message: granted
        ? 'Duurzame opslag is toegekend.'
        : 'De browser heeft dit verzoek niet toegekend.',
    });
  };

  const handleExportAll = async () => {
    setBusy(true);
    try {
      const metas = await listProjectMetas();
      const projects = [];
      for (const meta of metas) {
        const result = await loadProject(meta.id);
        if (result.status === 'ok') projects.push(result.project);
      }
      const blob = await exportAllProjectsBackup(projects);
      downloadBlob(blob, 'fotoboek-studio-backup.zip');
      pushToast({ kind: 'success', message: `${projects.length} project(en) geëxporteerd.` });
    } finally {
      setBusy(false);
    }
  };

  const handleImportFile = async (file: File) => {
    setBusy(true);
    try {
      const result = await importProjectBackup(file);
      if (result.status === 'ok') {
        pushToast({ kind: 'success', message: `“${result.project.title}” is geïmporteerd.` });
        navigate(`/editor/${result.project.id}`);
      } else {
        pushToast({ kind: 'error', message: result.message });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="page-shell">
      <SiteHeader />
      <div className="account-page">
        <h1>{t('account.title')}</h1>

        <section className="account-section">
          {authStatus === 'authenticated' && user ? (
            <>
              <p>{t('account.loggedInAs', { email: user.email })}</p>
              <Button variant="secondary" onClick={handleLogout}>
                {t('account.logout')}
              </Button>
            </>
          ) : (
            <p className="account-guest-notice">{t('account.guestNotice')}</p>
          )}
        </section>

        <section className="account-section">
          <h2>
            <HardDrive size={16} /> {t('account.storage.heading')}
          </h2>
          {estimate ? (
            <>
              <p>
                {t('account.storage.used', {
                  used: formatBytes(estimate.usageBytes),
                  quota: formatBytes(estimate.quotaBytes),
                })}
              </p>
              <p className={estimate.persisted ? 'account-storage-ok' : 'account-storage-warn'}>
                <ShieldCheck size={14} />
                {estimate.persisted
                  ? t('account.storage.persisted')
                  : t('account.storage.notPersisted')}
              </p>
              {!estimate.persisted ? (
                <Button variant="secondary" size="sm" onClick={handleRequestPersist}>
                  {t('account.storage.requestPersist')}
                </Button>
              ) : null}
            </>
          ) : (
            <Spinner size={16} />
          )}
        </section>

        <section className="account-section">
          <h2>{t('account.backup.heading')}</h2>
          <div className="account-backup-actions">
            <Button variant="secondary" onClick={handleExportAll} disabled={busy}>
              {busy ? <Spinner size={16} /> : <Download size={16} />}
              {t('account.backup.exportAll')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".fotoboek,application/zip"
              className="visually-hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleImportFile(file);
                e.target.value = '';
              }}
            />
            <Button
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
            >
              <Upload size={16} />
              {t('account.backup.importFile')}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
